import React from 'react';
import { get, isEqual, isNil } from 'lodash';
import moment from 'moment-timezone';

import themeVariables from '../assets/css/sass/_variables.scss?inline';
import { TrophyBronzeIcon, TrophyGoldIcon, TrophySilverIcon } from '../components/svg';
import { ARROW_TYPES, SHIFT_STATUSES } from '../constants/app.constants';

export const ThemeColors = {
  themeColor1: themeVariables['--theme-color-1']?.trim(),
  themeColor2: themeVariables['--theme-color-2']?.trim(),
  themeColor3: themeVariables['--theme-color-3']?.trim(),
  themeColor4: themeVariables['--theme-color-4']?.trim(),
  themeColor5: themeVariables['--theme-color-5']?.trim(),
  themeColor6: themeVariables['--theme-color-6']?.trim(),
  themeColor1_10: themeVariables['--theme-color-1-10']?.trim(),
  themeColor2_10: themeVariables['--theme-color-2-10']?.trim(),
  themeColor3_10: themeVariables['--theme-color-3-10']?.trim(),
  themeColor4_10: themeVariables['--theme-color-3-10']?.trim(),
  themeColor5_10: themeVariables['--theme-color-3-10']?.trim(),
  themeColor6_10: themeVariables['--theme-color-3-10']?.trim(),
  primaryColor: themeVariables['--primary-color']?.trim(),
  secondaryColor: themeVariables['--secondary-color']?.trim(),
  foregroundColor: themeVariables['--foreground-color']?.trim(),
  separatorColor: themeVariables['--separator-color']?.trim(),
  themeGreenColor: themeVariables['--theme-color-green']?.trim(),
  themeRedColor: themeVariables['--theme-color-red']?.trim(),
  themePurpleColor: themeVariables['--theme-color-purple']?.trim(),
  themePinkColor: themeVariables['--theme-color-pink']?.trim(),
  themeBlueColor: themeVariables['--theme-color-blue']?.trim(),
  themeOrangeColor: themeVariables['--theme-color-orange']?.trim(),
  themeGreenColor_10: themeVariables['--theme-color-green-10']?.trim(),
  themeRedColor_10: themeVariables['--theme-color-red-10']?.trim(),
  themePurpleColor_10: themeVariables['--theme-color-purple-10']?.trim(),
  themePinkColor_10: themeVariables['--theme-color-pink-10']?.trim(),
  themeBlueColor_10: themeVariables['--theme-color-blue-10']?.trim(),
  themeOrangeColor_10: themeVariables['--theme-color-orange-10']?.trim(),
};

/**
 *
 * @deprecated use @insightt-shared calculatePercentageDiff instead.
 */
export function calcChanges(current, prev) {
  if (!current && !prev) {
    return { percent: 0, status: ARROW_TYPES.up };
  }
  if (current && !prev) {
    return { percent: 100, status: ARROW_TYPES.up };
  }
  if (!current && prev) {
    return { percent: 100, status: ARROW_TYPES.down };
  }
  const percent = ((100 * (current - prev)) / prev).toFixed(2);
  const status = percent > 0 ? ARROW_TYPES.up : ARROW_TYPES.down;
  return {
    status,
    percent: Math.abs(percent) === 'NaN' ? 0 : Math.abs(percent),
  };
}

/**
 * @deprecated use @insightt-shared calculatePercentageDiff to get the status.
 */
export function getStatusArrow(status) {
  if (
    isEqual(ARROW_TYPES.up, status) ||
    status >= 0 ||
    isEqual(1, status) ||
    isEqual(0, status) ||
    isEqual(undefined, status)
  ) {
    return 'green';
  }
  if (isEqual(ARROW_TYPES.down, status) || isEqual(-1, status) || status < 0) {
    return 'red';
  }
  return null;
}

export function getRankTrophy(rank) {
  if (isEqual(1, rank)) return <TrophyGoldIcon />;
  if (isEqual(2, rank)) return <TrophySilverIcon />;
  if (isEqual(3, rank)) return <TrophyBronzeIcon />;

  return null;
}

export function validate(value, type) {
  if (!value) {
    return false;
  }
  let regex = '';
  switch (type) {
    case 'name':
      regex = /^[a-zA-Z]+$/;
      return regex.test(value);
    case 'email':
      regex =
        // eslint-disable-next-line
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return regex.test(String(value).toLowerCase());
    case 'phone':
      regex = /\D/g;
      return isEqual(value.replace(regex, '').length, 10);
    case 'address':
      regex = /^[a-zA-Z0-9\s]*$/;
      return regex.test(value);
    case 'city':
      regex = /^[a-zA-Z\s.-]*$/;
      return regex.test(value);
    case 'zip':
      regex = /^[0-9!-]*$/;
      return regex.test(value);
    case 'ssn':
      regex = /^[0-9]{0,4}$/;
      return regex.test(value);
    case 'password':
      regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&])[a-zA-Z0-9!@#$%^&]{8,}$/;
      return regex.test(value);
    default:
      return true;
  }
}

export function generateRandomPassword(length) {
  const chars = 'abcdefghijklmnopqrstuvwxyz!@#$%^&*()-+<>ABCDEFGHIJKLMNOP1234567890';
  let pass = '';
  for (let x = 0; x < length; x++) {
    const i = Math.floor(Math.random() * chars.length);
    pass += chars.charAt(i);
  }
  return pass;
}

export const areaChartTooltip = {
  mode: 'x',
  intersect: true,
  backgroundColor: ThemeColors.foregroundColor,
  titleFontColor: ThemeColors.primaryColor,
  borderColor: ThemeColors.separatorColor,
  borderWidth: 0.5,
  bodyFontColor: ThemeColors.primaryColor,
  bodySpacing: 10,
  xPadding: 15,
  yPadding: 15,
  cornerRadius: 0.15,
  displayColors: true,
  callbacks: {
    labelColor(tooltipItem, data) {
      const itemData = data.data.datasets[tooltipItem.datasetIndex];
      return {
        borderColor: itemData.pointHoverBorderColor,
        borderRadius: 3,
        backgroundColor: itemData.pointHoverBackgroundColor,
      };
    },
  },
};

export const mixChartTooltip = (datasetIndex, subStr) => ({
  mode: 'label',
  backgroundColor: ThemeColors.foregroundColor,
  titleFontColor: ThemeColors.primaryColor,
  borderColor: ThemeColors.separatorColor,
  borderWidth: 0.5,
  bodyFontColor: ThemeColors.primaryColor,
  bodySpacing: 10,
  xPadding: 15,
  yPadding: 15,
  cornerRadius: 0.15,
  callbacks: {
    label(tooltipItem, data) {
      const label = data.datasets[tooltipItem.datasetIndex].label || '';
      if (tooltipItem.datasetIndex >= datasetIndex) {
        return `${label}: ${tooltipItem.yLabel}${subStr}`;
      }
      return `${label}: ${tooltipItem.yLabel}`;
    },
  },
});

export const doughnutChartTooltip = {
  backgroundColor: ThemeColors.foregroundColor,
  titleFontColor: ThemeColors.primaryColor,
  borderColor: ThemeColors.separatorColor,
  borderWidth: 0.5,
  bodyFontColor: ThemeColors.primaryColor,
  bodySpacing: 10,
  xPadding: 15,
  yPadding: 15,
  cornerRadius: 0.15,
};

export const barChartTooltip = {
  backgroundColor: ThemeColors.foregroundColor,
  titleFontColor: ThemeColors.primaryColor,
  borderColor: ThemeColors.separatorColor,
  borderWidth: 0.5,
  bodyFontColor: ThemeColors.primaryColor,
  bodySpacing: 10,
  xPadding: 15,
  yPadding: 15,
  cornerRadius: 0.15,
};

export const centerTextPlugin = {
  afterDatasetsUpdate() {
    //
  },

  beforeDraw(chart) {
    const width = chart.chartArea.right;
    const height = chart.chartArea.bottom;
    const { ctx } = chart.chart;
    ctx.restore();

    let activeLabel = chart.data.labels[0];
    let activeValue = chart.data.datasets[0].data[0];
    let dataset = chart.data.datasets[0];
    let meta = dataset._meta[Object.keys(dataset._meta)[0]];
    let { total } = meta;

    let activePercentage = parseFloat(((activeValue / total) * 100).toFixed(1));
    activePercentage = chart.legend.legendItems[0].hidden ? 0 : activePercentage;

    if (chart.pointAvailable) {
      activeLabel = chart.data.labels[chart.pointIndex];
      activeValue = chart.data.datasets[chart.pointDataIndex].data[chart.pointIndex];

      dataset = chart.data.datasets[chart.pointDataIndex];
      meta = dataset._meta[Object.keys(dataset._meta)[0]];
      total = meta.total;
      activePercentage = parseFloat(((activeValue / total) * 100).toFixed(1));
      activePercentage = chart.legend.legendItems[chart.pointIndex].hidden ? 0 : activePercentage;
    }

    ctx.font = '36px Open Sans, sans-serif';
    ctx.fillStyle = ThemeColors.primaryColor;
    ctx.textBaseline = 'middle';

    const text = `${activePercentage}%`;
    const textX = Math.round((width - ctx.measureText(text).width) / 2);
    const textY = height / 2;
    ctx.fillText(text, textX, textY);

    ctx.font = '14px Open Sans, sans-serif';
    ctx.textBaseline = 'middle';

    const text2 = activeLabel;
    const textX2 = Math.round((width - ctx.measureText(text2).width) / 2);
    const textY2 = height / 2 - 30;
    ctx.fillText(text2, textX2, textY2);

    ctx.save();
  },

  beforeEvent(chart, event) {
    const firstPoint = chart.getElementAtEvent(event)[0];

    if (firstPoint) {
      chart.pointIndex = firstPoint._index;
      chart.pointDataIndex = firstPoint._datasetIndex;
      chart.pointAvailable = true;
    }
  },
};

export const durationBetweenTwoDates = (startDate, endDate) => {
  if (startDate && endDate) {
    // Convert the dates to moment objects
    const start = moment(startDate);
    const end = moment(endDate);

    // Calculate the difference in milliseconds
    const diff = end.diff(start);
    const momentDuration = moment.duration(diff);

    // Extract days, hours, minutes, and seconds
    const days = momentDuration.days();
    const hours = momentDuration.hours();
    const minutes = momentDuration.minutes();
    const seconds = momentDuration.seconds();

    // Return the formatted string
    return `${days}d : ${hours}h : ${minutes}m : ${seconds}s`;
  }

  return null;
};

export const getWeekRange = (date) => {
  const range = [];
  for (let index = 0; index < 7; index++) {
    range.push(moment(date).startOf('week').add(index, 'day').toDate());
  }
  return range;
};

export const getMonthRange = (date) => {
  const range = [];
  for (let index = 0; index < 32; index++) {
    const nextDay = moment(date).startOf('month').add(index, 'day');
    const lastDay = moment(date).endOf('month').format('YYYY-MM-DD');
    if (moment(nextDay.format('YYYY-MM-DD')).diff(moment(lastDay), 'day') >= 1) {
      break;
    }
    range.push(nextDay.toDate());
  }
  return range;
};

export const timeAgo = (date) => {
  const daysAgo = moment().diff(moment(date), 'days');
  if (daysAgo > 0) {
    return `${daysAgo} DAYS AGO`;
  }
  const hoursAgo = moment().diff(moment(date), 'hours');
  if (hoursAgo > 0) {
    return `${hoursAgo} HOURS AGO`;
  }
  const minutesAgo = moment().diff(moment(date), 'minutes');
  if (minutesAgo > 0) {
    return `${minutesAgo} MINUTES AGO`;
  }
  return `${moment().diff(moment(date), 'seconds')} SECONDS AGO`;
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const formatPercentage = (value) => {
  const percentage = typeof value === 'string' ? Number(value || '0') : value;
  return percentage.toFixed(1);
};

/**
 * @deprecated use @insightt-shared ShiftStatusColor instead.
 */
export const getShiftStatusColor = (status) => {
  if (status === SHIFT_STATUSES.working) {
    return '#13BF94';
  }
  if (status === SHIFT_STATUSES.breaking) {
    return '#F24949';
  }
  if (status === SHIFT_STATUSES.paused) {
    return '#FC9E3F';
  }
  if (status === SHIFT_STATUSES.ended) {
    return '#006aff';
  }
  return '#13BF94';
};

/**
 * @deprecated Don't add more sorts here, let's discuss if we can make a general solution for this
 * maybe using a separated function for each type of sort.
 * This function is being used to sort data everywhere in the app, this deprecation tag is to
 * stop adding more logic here, does not mean we have to remove it from legacy code.
 *
 * @param {*} unSortedData - unsorted data
 * @param {*} sortKey - sort key which is used to sort the array of objects
 * @param {*} sortOrder - sort order - 1 for assending, 0 for descending order. default is 1
 * @param {*} dataType - type of data to be sorted ex: string, number, date. default is 'string'
 * @returns sorted Data based on the sortOrder value and sortKey
 */

export const getSortedData = ({ unSortedData, sortKey, sortOrder = 1, dataType = 'string' }) =>
  // eslint-disable-next-line complexity
  [...unSortedData].sort((a, b) => {
    let x;
    let y;
    // 1 === ascending order, 0 === descending order

    if (sortKey === 'assignee') {
      x = `${a?.assignee?.firstName || ''} ${a?.assignee?.lastName || ''}`.toLowerCase();
      y = `${b?.assignee?.firstName || ''} ${b?.assignee?.lastName || ''}`.toLowerCase();

      if (sortOrder === 1) {
        return x.localeCompare(y);
      }
      return y.localeCompare(x);
    }

    if (dataType === 'string') {
      x = get(a, sortKey)?.toLowerCase() || '';
      y = get(b, sortKey)?.toLowerCase() || '';

      if (sortOrder === 1) {
        return x.localeCompare(y);
      }
      return y.localeCompare(x);
    }

    if (dataType === 'number') {
      x = Number(get(a, sortKey));
      y = Number(get(b, sortKey));

      // if NaN or infinity default to 0
      if (Number.isNaN(x) || !Number.isFinite(x)) {
        x = 0;
      }
      if (Number.isNaN(y) || !Number.isFinite(y)) {
        y = 0;
      }

      if (sortOrder === 0) {
        return x - y;
      }
      return y - x;
    }

    if (dataType === 'date') {
      x = get(a, sortKey);
      y = get(b, sortKey);
      if (sortOrder === 0) return new Date(y) - new Date(x);
      return new Date(x) - new Date(y);
    }

    if (sortOrder === 0) return x - y;
    return y - x;
  });

export const getValidationMessages = ({ formData, mandatoryFields }) => {
  const formDataWithValidationMessages = mandatoryFields.reduce((allErrorMessages, currentKey) => {
    const updatedMessages = { ...allErrorMessages };
    if (Array.isArray(formData[currentKey])) {
      if (!(formData[currentKey].length > 0)) {
        updatedMessages[currentKey] = 'This field is mandatory';
      }
    } else if (
      formData[currentKey] === null ||
      formData[currentKey] === undefined ||
      formData[currentKey] === ''
    ) {
      updatedMessages[currentKey] = 'This field is mandatory';
    }
    return updatedMessages;
  }, {});
  return formDataWithValidationMessages;
};

export const getDifferanceInSec = (startTime, endTime) => {
  const duration = moment.duration(moment(endTime).diff(startTime));
  const inSec = duration.asSeconds();
  let inPositiveSec = inSec;
  if (inSec < 0) {
    inPositiveSec = 24 * 60 * 60 + inSec;
  }
  return inPositiveSec;
};

export function getRandomNumber(min = 1, max = 100) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export const formatCount = (count) => {
  if (count / 1000 > 10) {
    return `${Math.floor(count / 1000).toString()}K+`;
  }
  return count;
};

export const getTimeZoneOffset = () => {
  const date = new Date();
  return date.getTimezoneOffset() / 60;
};

export const getTimeZoneOffsetFromTimeZone = (timezone) => moment().tz(timezone).parseZone().utcOffset() / -60;

/**
 * setFormatCurrency
 *
 * @param {number} number number to set format currency
 * @returns {string} number formatted
 */
export const setFormatCurrency = (number) => {
  if (isNil(number)) return '$0';

  const format = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'usd',
    minimumFractionDigits: 2,
  }).format(number);

  return `${format}`;
};

/**
 *
 * @param {number | string} value  - Value to check.
 * @returns {boolean} - true if value is numeric.
 */
export const isNumeric = (value) =>
  (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && !Number.isNaN(value);
